import React, {useEffect, useRef} from "react";
import {Container} from "reactstrap";
import IndexNavbar from "../../components/Navbars/IndexNavbar";
import InfoModal from "../../components/Modals/InfoModal";
import {useTranslation} from "react-i18next";
import axios from "axios";
import {getCookie} from "tools/tools";
import {evaluate, parse} from "mathjs";
import Footer from "../../components/Footers/IndexFooter";
import Plot from "react-plotly.js";

class FabricProperties {
    constructor(thermal_conductivity, heat_capacity, density, initial_temperature, radius) {
        this.thermal_conductivity = thermal_conductivity; // Теплопроводность k [Вт/(м·К)]
        this.heat_capacity = heat_capacity; // Теплоемкость c [Дж/(кг·К)]
        this.density = density; // Плотность ρ [кг/м³]
        this.initial_temperature = initial_temperature; // Начальная температура T₀ [°C]
        this.radius = radius;
    }
}

const defaultValues = {
    Ta: "5 + 10*sin(0.0001*t)", // Формула внешней температуры
    h: 6, // Коэффициент теплообмена
    diagnosticPoint: {x: 0, y: 0}, // Координаты диагностической точки
    fabrics: {
        scalp: new FabricProperties(0.34, 4000, 1000, 31, 98),
        skull: new FabricProperties(1.16, 2300, 1500, 34, 93),
        csf: new FabricProperties(0.618, 4174, 1003, 35, 88),
        surface_brain: new FabricProperties(0.503, 3700, 1050, 35.5, 86),
        deep_brain: new FabricProperties(0.503, 3700, 1050, 37.2, 69),
    },
};

const FABRICS = Object.keys(defaultValues.fabrics);

const validateUserExpression = (text, errors) => {
    const tValues = [0, 300]; // Аналог np.arange(0, 300, 300)
    const timeoutMilliseconds = 100; // Таймаут в миллисекундах

    // Симуляция интерпретатора с использованием mathjs
    const evaluateExpression = (expression, tValue) => {
        try {
            // Подставляем значение t в выражение
            const scope = {t: tValue};

            // Проверяем выражение заранее (на синтаксические ошибки)
            parse(expression);

            // Вычисляем результат
            const result = evaluate(expression, scope);

            // Проверяем, что результат является числом
            if (typeof result !== "number" || isNaN(result)) {
                errors.push("Expression should return a float value.");
                return null;
            }

            return result;
        } catch (e) {
            errors.push(`Error in expression: ${e.message}`);
            return null;
        }
    };

    try {
        // Проверяем каждое значение t с учётом таймаута
        for (let t of tValues) {
            const start = Date.now();
            const result = evaluateExpression(text, t);

            // Проверяем таймаут
            if (Date.now() - start > timeoutMilliseconds) {
                errors.push(`Expression timeout ${timeoutMilliseconds / 1000} seconds.`);
                break;
            }

            if (result === null) {
                break;
            }
        }
    } catch (e) {
        errors.push(`Unexpected error during validation: ${e.message}`);
    }
};

const validateValues = (values) => {
    const errors = {};

    // Валидация диагностической точки
    if (isNaN(values.diagnosticPoint.x) || values.diagnosticPoint.x < -100 || values.diagnosticPoint.x > 100) {
        errors.diagnosticPoint = {...errors.diagnosticPoint, x: "X должен быть числом в диапазоне от -100 до 100"};
    }
    if (isNaN(values.diagnosticPoint.y) || values.diagnosticPoint.y < -100 || values.diagnosticPoint.y > 100) {
        errors.diagnosticPoint = {...errors.diagnosticPoint, y: "Y должен быть числом в диапазоне от -100 до 100"};
    }

    // Валидация Ta и h
    const newErrors = [];
    validateUserExpression(values.Ta, newErrors);
    if (newErrors.length > 0)
        errors.Ta = newErrors;

    if (isNaN(values.h) || values.h <= 0) {
        errors.h = "Коэффициент h должен быть положительным числом";
    }

    // Валидация тканей
    FABRICS.forEach((fabric) => {
        Object.entries(values.fabrics[fabric]).forEach(([key, value]) => {
            if (isNaN(value) || value <= 0) {
                errors.fabrics = {
                    ...errors.fabrics,
                    [fabric]: {
                        ...errors.fabrics?.[fabric],
                        [key]: `${key} для ${fabric} должен быть положительным числом`,
                    },
                };
            }
        });
    });

    return errors;
};

const SimpleFEMForm = (props) => {
    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const [calcErrors, setCalcErrors] = React.useState({});
    const {t} = useTranslation();
    const [values, setValues] = React.useState({...defaultValues});

    React.useEffect(() => {
        document.body.classList.add("bg-white");
        return () => {
            document.body.classList.remove("bg-default");
        };
    }, []);

    const handleChange = (e) => {
        const {name, value} = e.target;

        let updatedValues;

        if (["x", "y"].includes(name)) {
            updatedValues = {
                ...values,
                diagnosticPoint: {
                    ...values.diagnosticPoint,
                    [name]: value,
                },
            };
        } else if (name === "Ta" || name === "h") {
            updatedValues = {
                ...values,
                [name]: value,
            };
        } else {
            const [fabric, param] = name.split("$");
            updatedValues = {
                ...values,
                fabrics: {
                    ...values.fabrics,
                    [fabric]: {
                        ...values.fabrics[fabric],
                        [param]: value,
                    },
                },
            };
        }

        setValues(updatedValues);
        const validationErrors = validateValues(updatedValues);
        setCalcErrors(validationErrors);
    };


    const handleSubmit = () => {
        const errors = validateValues(values);
        if (Object.keys(errors).length > 0) {
            setCalcErrors(errors);
            setModalIsOpen(true);
            return;
        }

        const payload = {
            Ta: values.Ta,
            h: values.h,
            diagnosticPoint: values.diagnosticPoint,
            fabrics: values.fabrics,
        };
        const formData = new FormData();
        formData.append("data", JSON.stringify(payload));
        console.log(formData);
        axios
            .post("/api/simple-fem-calc", formData, {
                headers: {
                    "X-CSRFToken": getCookie("csrftoken"),
                },
                withCredentials: true,
            })
            .then((response) => {
                console.log("Response from server:", response.data);
                //alert(t("CalculationСompleted"));
            })
            .catch((error) => {
                console.error("Error from server:", error.response?.data || error.message);
                setCalcErrors(error.response?.data);
                setModalIsOpen(true);
            });
    };

    const renderError = (error) => {
        if (!error) return null;
        return <small className="text-danger">{error}</small>;
    };

    const errorModalBody = () => {
        return calcErrors == null || calcErrors.length === 0 ? (
            t("Calculation started")
        ) : (
            <ul>
                {calcErrors["error"] || t("Check the form carefully")}
            </ul>
        );
    };

    const socketRef = useRef(null);
    const [showPlot, setShowPlot] = React.useState(false);

    const [plotData, setPlotData] = React.useState([
        {x: [], y: [], name: "center"},
        {x: [], y: [], name: "surface"},
        {x: [], y: [], name: "Ta"},
    ]);

    useEffect(() => {
        // Create WebSocket connection
        const loc = window.location;
        let wsStart = `ws://${loc.host}/ws/simple_3d/`;
        if (loc.protocol === 'https:') {
            wsStart = `wss://${loc.host}:9002/ws/simple_3d/`;
        }
        socketRef.current = new WebSocket(wsStart);

        // Handle WebSocket messages
        socketRef.current.onmessage = (event) => {
            const message = JSON.parse(JSON.parse(event.data));

            if (message.state === 'ready') {
                setPlotData(prev => prev.map(trace => ({...trace, x: [], y: []})));
                setShowPlot(true);
                setTimeout(() => {
                    const chartContainer = document.getElementById('chart-container');
                    if (chartContainer) {
                        chartContainer.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start'
                        });
                    }
                }, 100);
            } else if (message.state === 'processing') {
                const [time, center, surf, Ta] = message.data;
                setPlotData(prev => {
                    const newData = [...prev];
                    const maxPoints = 300;

                    newData.forEach((trace, idx) => {
                        const newX = [...trace.x, time];
                        const newY = [...trace.y, [center, surf, Ta][idx]];

                        if (newX.length > maxPoints) {
                            newX.shift();
                            newY.shift();
                        }

                        newData[idx] = {...trace, x: newX, y: newY};
                    });

                    return newData;
                });
            } else if (message.state === 'completed') {
                console.log('Calculation completed');
            } else if (message.state === 'error') {
                console.error('Error:', message.error);
                setShowPlot(false);
                const errorMessage = document.createElement('div');
                errorMessage.className = 'alert alert-danger';
                errorMessage.role = 'alert';
                errorMessage.textContent = `Error while calculating: ${message.error}`;
                const parent = document.querySelector('#result');
                if (parent) {
                    parent.appendChild(errorMessage);
                }
            }
        };

        socketRef.current.onclose = () => {
            console.error('WebSocket connection closed');
        };

        socketRef.current.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        // Cleanup WebSocket and Plotly when component unmounts
        return () => {
            if (socketRef.current) {
                socketRef.current.close();
            }
        };
    }, []);

    return (
        <div className="content">
            <IndexNavbar {...props} brandText={t("Simple FEM Calculator")}/>
            <InfoModal
                isOpen={modalIsOpen}
                modalTitle={t("Validation Errors")}
                onRequestClose={() => setModalIsOpen(false)}
                body={errorModalBody}
            />
            <div className="section section-components" style={{paddingTop: 40}}>
                <div className="container mb-5 pb-100">
                    <h1 className="h1 text-success font-weight-bold mb-4">{t("Simple FEM")}</h1>
                    <h2 className="text-muted">{t("inputDataParameters")}</h2>

                    <div className="form-group row mt-4">
                        <label className="col-sm-4 col-form-label">{t("diagnosticPoint")}:</label>
                        <div className="col-sm-8">
                            {["x", "y"].map((coord) => (
                                <div className="form-group row" key={coord}>
                                    <label htmlFor={coord} className="col-sm-6 col-form-label">
                                        {coord.toUpperCase()} {t("Coordinate")} (mm)
                                    </label>
                                    <div className="col-sm-6">
                                        <input
                                            id={coord}
                                            name={coord}
                                            type="number"
                                            defaultValue={values.diagnosticPoint[coord]}
                                            step="0.0001"
                                            className="form-control"
                                            onChange={handleChange}
                                        />
                                        {renderError(calcErrors["diagnosticPoint"]?.[coord])}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="form-group row">
                        <label htmlFor="h" className="col-sm-8 col-form-label">
                            {t("HeatTransferCoefficient")}
                        </label>
                        <div className="col-sm-4">
                            <input
                                id="h"
                                name="h"
                                type="number"
                                defaultValue={values.h}
                                step="0.01"
                                className="form-control"
                                onChange={handleChange}
                            />
                            {renderError(calcErrors["h"])}
                        </div>
                    </div>

                    <div className="form-group row">
                        <label htmlFor="Ta" className="col-sm-8 col-form-label">
                            {t("AmbientTemperatureF")}
                        </label>
                        <div className="col-sm-4">
                            <input
                                id="Ta"
                                name="Ta"
                                type="text"
                                defaultValue={values.Ta}
                                className="form-control"
                                onChange={handleChange}
                            />
                            {renderError(calcErrors["Ta"])}
                        </div>
                    </div>

                    <hr className="my-4"/>
                    <h2 className="text-muted">{t("PropertiesOfFabrics")}</h2>

                    {FABRICS.map((fabric) => (
                        <React.Fragment key={fabric}>
                            <div className="form-group row">
                                <label className="col-sm-4 col-form-label font-weight-bold">
                                    {t(fabric)}
                                </label>
                                <div className="col-sm-8">
                                    {Object.keys(defaultValues.fabrics[fabric]).map((param) => (
                                        <div className="form-group row" key={`${fabric}.${param}`}>
                                            <label htmlFor={`${fabric}$${param}`} className="col-sm-7 col-form-label">
                                                {t(param)}
                                            </label>
                                            <div className="col-sm-5">
                                                <input
                                                    id={`${fabric}$${param}`}
                                                    name={`${fabric}$${param}`}
                                                    type="number"
                                                    defaultValue={values.fabrics[fabric][param]}
                                                    step="0.01"
                                                    className="form-control"
                                                    onChange={handleChange}
                                                />
                                                {renderError(calcErrors["fabrics"]?.[fabric]?.[param])}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </React.Fragment>
                    ))}

                    <button className="btn btn-secondary mb-2" onClick={handleSubmit}>
                        {t("Calc")}
                    </button>
                    {showPlot && (
                        <Container>
                            <div className="alert alert-success" role="alert">
                                {t('CalculationCompleted')}
                            </div>
                        </Container>
                    )}

                </div>
                {showPlot && (
                    <div id="chart-container" style={{display: 'flex', justifyContent: 'center'}}>
                        <Plot
                            data={plotData.map(trace => ({
                                ...trace,
                                mode: 'lines',
                                type: 'scatter',
                                name: t(trace.name)
                            }))}
                            layout={{
                                title: t("RealTimeData"),
                                xaxis: {title: t("Time"), nticks: 48},
                                yaxis: {title: t("Temperature"), nticks: 20},
                                showlegend: true,
                            }}
                            style={{width: '300%', height: '90vh'}}
                            config={{responsive: true}}
                            useResizeHandler={true}
                        />
                    </div>
                )}
            </div>
            <Container style={{backgroundColor: '#f4f5f7'}} fluid>
                <Footer/>
            </Container>
        </div>
    );
};

export default SimpleFEMForm;
